import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ScrappyDB from "./ScrappyDB";
import { logout } from '../../redux/actions/authActions';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";

const ScrappyHome = () => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn } = authState;
  const [userName, setUserName] = useState(authState.user.name);
  const [showPrograms, setShowPrograms] = useState(false);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/mindspark");
  };

  const features = [
  {
    title: "Immersive Reading & Speaking Books",
    emoji: "📖",
    desc: "An innovative platform. Read. Listen, Download, Share, Test, Certificate!"
  },
  {
    title: "Chronicle – Digital Diary",
    emoji: "📔",
    desc: "Can log milestones, emotions and daily events to create a yearly memory book."
  },
  {
    title: "Applause – Celebrate Growth",
    emoji: "👏",
    desc: "Capture and cherish achievements with Applause entries for lifelong memories."
  },
  {
    title: "Daily Routine Framework",
    emoji: "🕰️",
    desc: "An hour-by-hour guide to balance nutrition, learning, play and rest."
  },
  {
    title: "Think5Whys Reflection",
    emoji: "🧠",
    desc: "A simple tool to reflect on challenges and uncover deeper insights in growing years."
  },
  {
    title: "Gamification & Rewards",
    emoji: "🏆",
    desc: "Earn points, rise on the leaderboard and compete for 'Youth of the Month' awards."
  },
  {
    title: "Community of Readers",
    emoji: "🤝",
    desc: "Connect with like-minded readers, share stories and learn together on the Scrappy platform."
  },
  {
    title: "Scrappy Certificate",
    emoji: "📜",
    desc: "Receive recognition for completing your immersive journey."
  },
  {
    title: "Yearly Subscription Bundle",
    emoji: "💫",
    desc: "All features – book, Chronicle, Applause, tools & awards – under one affordable annual plan."
  }
];


  return (
    <div className="font-sans text-gray-800 bg-gray-100">
      {/* Header */}
      <header className="flex justify-between items-center px-6 py-4 bg-purple-700 shadow-md sticky top-0 z-50">
        <div className="text-2xl font-extrabold text-yellow-200 tracking-wide">Scrappy</div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex space-x-6 font-medium text-gray-700">
          <a href="#whyus" className="text-white hover:text-purple-200 transition">About</a>
          <Link to="/udyam-updates" className="text-white hover:text-purple-200 transition">Updates</Link>
        </nav>

        <div className="hidden md:flex items-center space-x-4">
          {isLoggedIn ? (
            <>
              <span className="text-purple-200 font-medium">Welcome, {userName}</span>
              <button
                onClick={() => navigate(`/mindsparkdashboard`)}
                className="w-9 h-9 flex items-center justify-center text-lg bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
                title="Dashboard"
              >
                🏠
              </button>
              <button
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center text-lg bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 text-sm border border-purple-500 text-purple-600 rounded-full hover:bg-purple-50 transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-full hover:bg-purple-700 transition"
              >
                Register
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden flex items-center justify-center w-24">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-3xl text-purple-600 focus:outline-none"
            aria-label="Toggle Menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white px-6 py-4 space-y-1 text-center shadow-md">
          {isLoggedIn && (
            <div className="flex flex-col items-center text-gray-800">
              <p className="text-purple-600 font-medium">Welcome, {userName}</p>
              <div className="flex space-x-4 mt-2">
                <button
                  onClick={() => navigate(`/mindsparkdashboard`)}
                  className="w-10 h-10 flex items-center justify-center text-lg bg-purple-500 text-white rounded-full hover:bg-purple-600 transition"
                >
                  🏠
                </button>
                <button
                  onClick={handleLogout}
                  className="w-10 h-10 flex items-center justify-center text-lg bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                >
                  ⏻
                </button>
              </div>
            </div>
          )}
          <a href="#whyus" className="block hover:text-purple-600">About Scrappy</a>
          <Link to="/mindsparkupdates" className="block hover:text-purple-600">News</Link>

          {!isLoggedIn && (
            <div className="flex justify-center space-x-4 pt-2">
              <button className="px-4 py-2 text-sm border border-purple-500 text-purple-600 rounded-full hover:bg-purple-50 transition">Login</button>
              <button className="px-4 py-2 text-sm bg-purple-600 text-white rounded-full hover:bg-purple-700 transition">Register</button>
            </div>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{
          backgroundImage: "url('/assets/banners/mindspark.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-4 sm:px-6 md:px-10 lg:px-16 py-4 w-full max-w-6xl mx-auto">
          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-2 tracking-wide text-yellow-200">
            Scrappy
          </h1>
          <p className="text-lg md:text-xl text-center text-gray-200 mb-2 font-medium">
            Nothing Goes Waste. Recycle. Resell. Reward.
          </p>
          <p className="text-m md:text-m text-center text-blue-200 mb-2 font-medium">
            Win Prizes for Scrap Selling
          </p>
          <div className="flex flex-col items-center justify-center bg-gray-900/90 p-2 shadow-lg rounded-2xl ">
            {/* Section 1 */}
            <div className="bg-gray-900/90 p-1 shadow-lg">
              <div className="flex flex-row gap-3">
                {/* <h2 className="text-m text-purple-200 font-semibold mb-4">My:</h2> */}
                <a href="/bookshelf" className="hover:text-purple-400 transition underline">My Scrappy Scorecard</a>
                <a href="/rankings" className="hover:text-purple-400 transition underline">Scrappy Ranking</a>
                <a href="/mindsparkdb" className="hover:text-purple-400 transition underline">Scrappy Winners</a>
              </div>
            </div>

            {/* Section 2 */}
            <div className="w-full bg-gray-500 p-2 rounded-2xl shadow-lg items-center">
              <div className="flex flex-row justify-center items-center gap-2 mb-0">
                <h2 className="text-m text-purple-200 font-semibold mb-2">Scrappy Collectors:</h2>
                <select className="h-10 px-3 py-2 rounded-lg bg-gray-800 text-sm">
                  <option>City</option>
                  <option>Mumbai</option>
                  <option>Nashik</option>
                </select>
                <select className="h-10 px-3 py-2 rounded-lg bg-gray-800 text-sm">
                  <option>Area</option>
                  <option>Goregaon</option>
                  <option>Andheri</option>
                </select>
                <select className="h-10 px-3 py-2 rounded-lg bg-gray-800 text-sm">
                  <option>Neighbourhood</option>
                  <option>Goregaon East</option>
                  <option>Goregaon West</option>
                </select>
                <button onClick={() => navigate("/orderflow-products")} className="w-16 h-10 px-4 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm mt-4">
                  View
                </button>
                 {/* Toggle Icon */}
                {/* <button
                  onClick={() => setShowPrograms((prev) => !prev)}
                  className="mt-4 w-16 bg-gray-800 hover:bg-gray-700 rounded-lg"
                >
                  <FontAwesomeIcon
                    icon={faLink}
                    className={`w-8 h-4 ${
                      showPrograms ? "text-purple-400" : "text-gray-400"
                    }`}
                  />
                </button> */}
              </div>
              {/* {showPrograms && (
                <div className="bg-gray-900/90 p-4 shadow-lg flex justify-center mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 text-center text-white max-w-6xl">
                    {[
                      "Teachable",
                      "MATRIKA",
                      "GOLDEN7",
                      "YUVASolver",
                      "LEGEND",
                      "BLAZE",
                      "RISE",
                      "BEAUTI",
                      "ROUTE",
                      "CANDOR",
                      "COUTUR",
                      "SkillGrill",
                    ].map((item, idx) => (
                      <button
                        key={idx}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-center justify-center"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )} */}
            </div>

            {/* <div className="bg-gray-900/90 p-1 shadow-lg">
              <div className="py-2 flex flex-row items-center justify-center gap-1 text-sm">
                <h2 className="text-m text-purple-200 font-semibold mb-2">Enroll As:</h2>
                {["Author", "Publisher", "Institution", "Teacher", "Educationist", "Corporate", "PBO"].map((role, idx) => (
                  <a
                    key={idx}
                    href={`/${role.toLowerCase()}`}
                    className="hover:text-purple-400 transition underline"
                  >
                    {role}
                  </a>
                ))}
              </div>
            </div> */}

            {/* Section 4 */}
            <div className="bg-gray-900/90 p-1 shadow-lg">
              <div className="flex flex-row gap-3 mb-1">
                <h3 className="text-m text-purple-200 font-medium mb-1">Pocket Money / Earning:</h3>
                <a href="/orderflow-earning-estimator" className="hover:text-purple-400 transition underline">Refer</a>
                <a href="/orderflow-earning-estimator" className="hover:text-purple-400 transition underline">Resell</a>
                {/* <a href="/bookshelf" className="hover:text-purple-400 transition underline">Readfluence</a> */}
                <a href="/visigift-home" className="hover:text-purple-400 transition underline">Rewarding Ads</a>
                <a href="/OrderFlowSpotBuy" className="hover:text-purple-400 transition underline">Rewarding Videos</a>
                <a href="#" className="hover:text-purple-400 transition underline">Become Scrappy Collector</a>
                <a href="#" className="hover:text-purple-400 transition underline">Become Scrappy Angel</a>
              </div>
            </div>

             {/* Section 5 */}
            <div className="bg-gray-900/90 p-1 shadow-lg">
              <div className="flex flex-row gap-3">
                <h2 className="text-m text-purple-200 font-semibold mb-1">Commerce Zone:</h2>
                <a href="#" className="hover:text-purple-400 transition underline">Collection Points</a>
                {/* <a href="/thinkxpresshome" className="hover:text-purple-400 transition underline">ThinkXpress</a> */}
                <a href="/nearbydeals" className="hover:text-purple-400 transition underline">Nearby Scrap Dealers</a>
                {/* <a href="/think-rent-home" className="hover:text-purple-400 transition underline">Rental</a> */}
                <a href="/thinkwholesale-home" className="hover:text-purple-400 transition underline">Wholesale</a>
                {/* <a href="/orderflow-my-mlm-request-list" className="hover:text-purple-400 transition underline">Sell</a> */}
              </div>
            </div>

            {/* Section 6 */}
            {/* <div className="bg-gray-900/90 p-1 shadow-lg">
              <div className="flex flex-row gap-3">
                <h2 className="text-m text-purple-200 font-semibold mb-1">Fun & Rewards:</h2>
                <a href="/applause-hub-home" className="hover:text-purple-400 transition underline">Marksheet Contest</a>
                <a href="/kharchojeeto" className="hover:text-purple-400 transition underline">Kharcho Jeeto</a>
                <a href="#" className="hover:text-purple-400 transition underline">Aalu Dalo Sona Nikalo</a>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Scrappy Certificate */}
      {/* <ScrappyDB /> */}

      <section id="whyus" className="py-4 px-6 md:px-20 bg-gradient-to-r from-purple-600 to-purple-700">
        <h2 className="text-xl font-bold text-center mb-2 text-white">Why Choose Scrappy?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-center text-white">
          {features.map((feature, idx) => (
            <div key={idx} className="bg-white/10 p-2 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
              <div className="text-xl mb-5">{feature.emoji}</div>
              <h4 className="text-lg font-semibold mb-3">{feature.title}</h4>
              <p className="text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

     {/* Scrappy – Growth, Engagement & Empowerment */}
      <section id="mindspark" className="py-8 px-6 md:px-20 bg-gradient-to-r from-purple-700 to-purple-800 text-white">
        <h2 className="text-xl font-bold text-center mb-2">Scrappy – Your Daily Growth & Reflection Buddy</h2>
        <div className="max-w-6xl mx-auto space-y-4">

          {/* Core Features */}
          <div className="bg-white/10 p-2 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
            <h3 className="text-m font-semibold mb-3">✨ Core Features</h3>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><strong>📝 Chronicle</strong> — Daily logs for thoughts, moods, study habits, and personal reflections.</li>
              <li><strong>👏 Applause</strong> — Celebrate your wins (big or small), appreciate friends, family & peers.</li>
              <li><strong>🔍 Root Cause Analysis (RCA)</strong> — Learn to ask “Why?” multiple times, turn confusion into clarity, build a problem-solving mindset.</li>
              <li><strong>📖 Diary / Yearbook</strong> — Auto-compiled from Chronicle, Applause & RCA → downloadable memory book of your school/college years.</li>
            </ul>
          </div>

          {/* Engagement & Recognition */}
          <div className="bg-white/10 p-2 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
            <h3 className="text-m font-semibold mb-1">✨ Celebrate Wins, Stay Inspired</h3>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><strong>💳 Reader ID Card</strong> — Create & share your unique digital identity.</li>
              <li><strong>⚡ Brain Fuel Quizzes</strong> — Daily 3-question challenges (general knowledge, current affairs, creativity, mental health).</li>
              <li><strong>📰 Youth Knowledge</strong> — Daily top 10 youth-friendly updates from the world.</li>
              <li><strong>🏆 Leaderboard & Rewards</strong> — Track activities, earn points, win exciting prizes & badges.</li>
              <li><strong>🎙️ Workshops & Podcasts</strong> — Interactive talks on studies, career, relationships, creativity & self-growth.</li>
              <li><strong>📊 Report Card Challenge</strong> — Log your academic + personal progress, compete in friendly contests.</li>
              <li><strong>🎮 Scrappy Games</strong> — Quick, fun brain teasers & logic puzzles to sharpen thinking.</li>
            </ul>
          </div>

          {/* Counselling, Commerce & Contribution */}
          <div className="bg-white/10 p-2 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
            <h3 className="text-m font-semibold mb-1">✨ Empowering Readers & Writers Beyond Reflection</h3>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><strong>🤖 AI Buddy Counselling</strong> — 24x7 judgment-free support for studies, friendships, stress, or career doubts.</li>
              <li><strong>👩‍🏫 Expert Mentoring</strong> — Pay-per-minute access to real mentors, teachers & career guides.</li>
              <li><strong>👛 Scrappy Wallet</strong> — Hassle-free payments & prize redemptions.</li>
              <li><strong>👭 Referral & Ambassador Program</strong> — Earn points/rewards by inviting friends (multi-level rewards).</li>
              <li><strong>🛒 Marketplace</strong> — Buy/sell products, books, art & digital creations.</li>
              <li><strong>🎁 Scrappy Collectibles</strong> — Earn, trade, gift or sell digital collectibles & merch.</li>
              <li><strong>⏱️ Focus Timer</strong> — Stay productive with Pomodoro-style focus sessions.</li>
              <li><strong>📝 Creator Hub (Upcoming)</strong> — Share writings, videos, art & monetize your talent.</li>
              <li><strong>📊 Dashboard</strong> — Visualize moods, habits, growth & progress in one place.</li>
              <li><strong>🌍 Nation Builder</strong> — Every learning, reflection & activity adds to your personal growth → and Bharat’s future.</li>
            </ul>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="bg-purple-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-purple-400 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-purple-400 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-purple-400 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-purple-400 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ScrappyHome;
